import { useState, useEffect, createContext, useContext } from 'react';
import { Menu, Globe } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { SiLinkedin } from 'react-icons/si';
import { translations, CONTACT_INFO, ROUTE_PATHS, type Language } from '@/lib/index';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

interface LayoutProps {
  children: React.ReactNode;
}

const LanguageContext = createContext<{
  language: Language;
  setLanguage: (lang: Language) => void;
}>({ language: 'fr', setLanguage: () => {} });

export const useLanguage = () => useContext(LanguageContext);

export function Layout({ children }: LayoutProps) {
  const [language, setLanguage] = useState<Language>('fr');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const t = translations[language];

  useEffect(() => {
    if (language === 'ar') {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = 'ar';
    } else {
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = language;
    }
  }, [language]);

  const navItems = [
    { label: t.nav.home, to: ROUTE_PATHS.HOME },
    { label: t.nav.about, to: ROUTE_PATHS.ABOUT },
    { label: t.nav.services, to: ROUTE_PATHS.SERVICES },
    { label: t.nav.expertise, to: ROUTE_PATHS.EXPERTISE },
    { label: t.nav.contact, to: ROUTE_PATHS.CONTACT },
  ];

  const languageOptions: { code: Language; label: string; flag: string }[] = [
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'zh', label: '中文', flag: '🇨🇳' },
    { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  ];

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
    <div className="min-h-screen flex flex-col bg-background">
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link
              to={ROUTE_PATHS.HOME}
              className="text-xl font-semibold text-foreground hover:text-primary transition-colors"
            >
              Kyra AVM
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === item.to
                      ? 'text-primary font-semibold'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="hidden md:flex">
                    <Globe className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {languageOptions.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className={language === lang.code ? 'bg-accent' : ''}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      {lang.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                  <nav className="flex flex-col gap-6 mt-8">
                    {navItems.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={`text-lg font-medium transition-colors ${
                          location.pathname === item.to
                            ? 'text-primary font-semibold'
                            : 'text-foreground hover:text-primary'
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                    <div className="pt-6 border-t border-border">
                      <p className="text-sm font-medium text-muted-foreground mb-3">
                        Langue / Language
                      </p>
                      <div className="flex flex-col gap-2">
                        {languageOptions.map((lang) => (
                          <Button
                            key={lang.code}
                            variant={language === lang.code ? 'secondary' : 'ghost'}
                            className="justify-start"
                            onClick={() => setLanguage(lang.code)}
                          >
                            <span className="mr-2">{lang.flag}</span>
                            {lang.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 pt-16">{children}</main>

      <footer className="bg-muted/30 border-t border-border mt-24">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Kyra AVM Consulting
              </h3>
              <p className="text-sm text-muted-foreground">
                {t.about.description.slice(0, 120)}...
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                {t.contact.title}
              </h3>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{t.contact.phone}:</span>{' '}
                  <a
                    href={`tel:${CONTACT_INFO.phone}`}
                    className="hover:text-primary transition-colors"
                  >
                    {CONTACT_INFO.phone}
                  </a>
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{t.contact.email}:</span>{' '}
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="hover:text-primary transition-colors"
                  >
                    {CONTACT_INFO.email}
                  </a>
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                {t.footer.quickLinks}
              </h3>
              <div className="flex flex-col gap-2">
                {navItems.slice(0, 4).map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                {t.footer.followUs}
              </h3>
              <div className="flex gap-4">
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                  aria-label="LinkedIn"
                >
                  <SiLinkedin className="h-6 w-6" />
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              {t.footer.copyright} • {t.footer.allRightsReserved}
            </p>
          </div>
        </div>
      </footer>
    </div>
    </LanguageContext.Provider>
  );
}
